import { BlueprintLocation, Building, BuildingStructure, PhotoModel } from '../types/protocols';
import { BaseComponentImpl, RoomImpl } from '../types/implementations';
import { ParkingArea } from '../types/parking';
import { StorageUnit } from '../types/storageUnits';
import { NumberedMarker } from '../Components/BlueprintWithMarkers';
import { getAllComponentsForRooms, roomlessComponentsOnDrawing } from './ComponentRoomUtils';
import { naturalCompare } from './naturalSort';
import {
    areasOnLevel,
    isSpotRented,
    parkingSpotDisplayName,
    sortedParkingSpots,
    spotHasCharger,
    spotStatusColor,
    spotTypeIcon,
} from './parkingUtils';
import { STORAGE_UNIT_ICON, storageUnitStatusColor, isUnitRented, sortedStorageUnits, storageUnitDisplayName, unitsOnLevel } from './storageUnitUtils';
import { roofPlanComponents } from './roofPlan';
import { roomIsOnLevel } from './roomUtils';

/**
 * Pure helpers for the blueprint relocation wizard: which markers sit on the
 * drawing being replaced, in what order, and where to propose them on the
 * new drawing. No Firebase, no React.
 */

export type RelocatableKind = 'room' | 'component' | 'storageUnit' | 'parkingSpot';

/**
 * One marker to move. `dbPath` is the entity node (no leading slash); the
 * commit writes `${dbPath}/blueprintLocations/{newImageKey}`. Exactly one of
 * `room`, `component`, `numbered` is set and drives rendering.
 */
export interface RelocatableMarker {
    id: string;
    kind: RelocatableKind;
    entityKey: string;
    displayName: string;
    dbPath: string;
    sourceLocation: BlueprintLocation;
    room?: RoomImpl;
    component?: BaseComponentImpl;
    numbered?: NumberedMarker;
}

const bySortKey = (a: string, b: string, aKey: string, bKey: string): number => {
    const byName = naturalCompare(a, b);
    return byName !== 0 ? byName : aKey.localeCompare(bKey);
};

const roomItems = (buildingKey: string, rooms: RoomImpl[], imageKey: string): RelocatableMarker[] =>
    rooms
        .filter(room => !!room.blueprintLocations?.[imageKey])
        .sort((a, b) => bySortKey(a.number || a.label || '', b.number || b.label || '', a.key, b.key))
        .map(room => ({
            id: `room-${room.key}`,
            kind: 'room',
            entityKey: room.key,
            displayName: room.displayName(),
            dbPath: `buildings/${buildingKey}/structure/rooms/${room.key}`,
            sourceLocation: room.blueprintLocations![imageKey],
            room,
        }));

const componentItems = (building: Building, components: BaseComponentImpl[], imageKey: string): RelocatableMarker[] =>
    components
        .filter(component => !!component.blueprintLocations?.[imageKey])
        .map(component => ({ component, name: component.displayName(building) }))
        .sort((a, b) => bySortKey(a.name, b.name, a.component.key, b.component.key))
        .map(({ component, name }) => ({
            id: `component-${component.key}`,
            kind: 'component',
            entityKey: component.key,
            displayName: name,
            dbPath: `buildings/${building.key}/structure/components/${component.key}`,
            sourceLocation: component.blueprintLocations![imageKey],
            component,
        }));

const unitItems = (buildingKey: string, units: StorageUnit[], imageKey: string): RelocatableMarker[] =>
    sortedStorageUnits(units)
        .filter(unit => !!unit.blueprintLocations?.[imageKey])
        .map(unit => ({
            id: `storageUnit-${unit.key}`,
            kind: 'storageUnit',
            entityKey: unit.key,
            displayName: storageUnitDisplayName(unit),
            dbPath: `buildings/${buildingKey}/storageUnits/${unit.key}`,
            sourceLocation: unit.blueprintLocations![imageKey],
            numbered: {
                key: unit.key,
                number: unit.number,
                color: storageUnitStatusColor(unit),
                icon: STORAGE_UNIT_ICON,
                iconColored: true,
                isRented: isUnitRented(unit),
                displayName: storageUnitDisplayName(unit),
                blueprintLocations: unit.blueprintLocations,
            },
        }));

const spotItems = (buildingKey: string, areas: ParkingArea[], imageKey: string): RelocatableMarker[] =>
    areas.flatMap(area =>
        sortedParkingSpots(area)
            .filter(spot => !!spot.blueprintLocations?.[imageKey])
            .map(spot => ({
                id: `parkingSpot-${area.key}-${spot.key}`,
                kind: 'parkingSpot' as const,
                entityKey: spot.key,
                displayName: parkingSpotDisplayName(spot),
                dbPath: `buildings/${buildingKey}/parking/${area.key}/spots/${spot.key}`,
                sourceLocation: spot.blueprintLocations![imageKey],
                numbered: {
                    key: spot.key,
                    number: spot.number,
                    color: spotStatusColor(spot),
                    icon: spotTypeIcon(spot.type),
                    glyphs: [
                        ...(spotHasCharger(spot) ? ['charger' as const] : []),
                        ...(spot.hasOutlet === true ? ['outlet' as const] : []),
                    ],
                    isRented: isSpotRented(spot),
                    displayName: parkingSpotDisplayName(spot),
                    blueprintLocations: spot.blueprintLocations,
                },
            }))
    );

/**
 * Markers placed on a level's drawing `imageKey`: rooms on the level (a room
 * spanning several levels on each of them), the components of those rooms,
 * room-less components placed on it (a balcony), storage units on the level,
 * and spots of level-mode garages resolving to the level. Lägenheter/lokaler have no pins of their own (units plan D5):
 * their rooms are relocated here and the unit pin follows.
 * Order: rooms → components → storage units → spots, each by number/name.
 */
export const levelRelocationItems = (
    building: Building,
    levelKey: string,
    imageKey: string,
    units: StorageUnit[],
    areas: ParkingArea[]
): RelocatableMarker[] => {
    const structure: BuildingStructure | undefined = building.model.structure;
    const rooms = Object.values(structure?.rooms || {})
        .filter(room => room && roomIsOnLevel(room, levelKey))
        .map(room => new RoomImpl(room));
    const components = [
        ...getAllComponentsForRooms(building, rooms.map(room => room.key)),
        ...roomlessComponentsOnDrawing(building, imageKey),
    ];
    return [
        ...roomItems(building.key, rooms, imageKey),
        ...componentItems(building, components, imageKey),
        ...unitItems(building.key, unitsOnLevel(units, levelKey), imageKey),
        ...spotItems(building.key, areasOnLevel(areas, structure, levelKey), imageKey),
    ];
};

/**
 * Markers placed on the roof drawing `imageKey`: the takplan components (D6)
 * that carry a position on it — roof, chimney, roof safety, takfönster and
 * roof-mounted utilities (D7). Ordered by display name.
 */
export const roofPlanRelocationItems = (building: Building, imageKey: string): RelocatableMarker[] =>
    componentItems(
        building,
        roofPlanComponents(building.model.structure).map(component => new BaseComponentImpl(component)),
        imageKey
    );

/** Spots of one parking area placed on `imageKey` (the area's resolved drawing). */
export const areaRelocationItems = (buildingKey: string, area: ParkingArea, imageKey: string): RelocatableMarker[] =>
    spotItems(buildingKey, [area], imageKey);

/**
 * Same relative position on the new image. Rescans of the same floor are
 * usually close to aligned, so this is the marker the user confirms with Enter.
 */
export const proposedLocation = (
    source: BlueprintLocation,
    target: Pick<PhotoModel, 'key' | 'width' | 'height'>
): BlueprintLocation => {
    const sourceWidth = source.width > 0 ? source.width : 1;
    const sourceHeight = source.height > 0 ? source.height : 1;
    return {
        key: target.key,
        x: Math.round((source.x / sourceWidth) * target.width),
        y: Math.round((source.y / sourceHeight) * target.height),
        width: target.width,
        height: target.height,
    };
};

/** Shallow copy that keeps the prototype (RoomImpl/BaseComponentImpl methods) and carries one location. */
export const withBlueprintLocation = <T extends object>(entity: T, imageKey: string, location: BlueprintLocation): T =>
    Object.assign(Object.create(Object.getPrototypeOf(entity)), entity, { blueprintLocations: { [imageKey]: location } });
